import { FC, Fragment, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Avatar } from '../../ui/Avatar/Avatar';
import { Transition } from '@headlessui/react';
import { usePrivy } from '@privy-io/react-auth';
import clsx from 'clsx';
import { t } from 'i18next';
import { EyeIcon } from 'lucide-react';

import { useSwitchChainHandler } from 'modules/Room/Hooks/useSwitchHandler';
import styles from 'modules/daily-quests-page/components/chest-icon/chest-icon.module.css';
import { usePrizeBoxStore } from 'modules/prize-box-page/store/prize-box-store';

import Dropdown from 'shared/components/ui/DropDown/DropDown';
import { MediaBtn } from 'shared/components/ui/buttons/media-button';
import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { InfiniteMaxProgressBar } from 'shared/components/ui/progress-bar/progress-bar';
import { chainIcons } from 'shared/config';
import { ROUTES } from 'shared/constants';
import { LANDING_PAGE_URL } from 'shared/constants/constants';
import { RoomStatusEnum } from 'shared/constants/shared-enums';
import { SOCIAL_MEDIA } from 'shared/constants/socialMedia';
import { useAccount, useDisconnect, useSwitchChain } from 'shared/hooks/use-web3';
import { useGlobalStore } from 'shared/store/globalStore';
import { useRoomStore } from 'shared/store/roomStore';
import { useUIStore } from 'shared/store/uiStore';
import { prettyTx } from 'shared/utils/pretty-tx';

import AboutIcon from 'assets/about-mobile.svg?react';
import ArrowDown from 'assets/arrow.svg?react';
import CardsIcon from 'assets/cards.svg?react';
import CloseXIcon from 'assets/close-x.svg?react';
import { default as Copy, default as CopyIcon } from 'assets/copy2.svg?react';
import IconDiscord from 'assets/discord.svg?react';
import GameIcon from 'assets/game.svg?react';
import LeaderboardIcon from 'assets/leaderboard.svg?react';
import LogoutIcon from 'assets/logout-mobile.svg?react';
import attention from 'assets/png/!.png?format=webp;avif&as=picture';
import PriceIcon from 'assets/prize.svg?react';
import QuestsIcon from 'assets/quests.svg?react';
import ReferralIcon from 'assets/referral.svg?react';
import BugIcon from 'assets/svg/bug.svg?react';
import IconTelegram from 'assets/telegram.svg?react';

import { MenuItem } from './menu-item';

interface IOverlayProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  sentryFeedbackRef?: RefObject<HTMLElement>;
  pointBalance?: any;
  activeQuests?: number;
}

export const Overlay: FC<IOverlayProps> = ({
  open,
  setOpen,
  sentryFeedbackRef,
  pointBalance,
  activeQuests,
}) => {
  const { chain, chainId, address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const { roomStatus, setShowHash, someoneElseGame } = useRoomStore();
  const leaderboardMap = useGlobalStore((state) => state.leaderboardMap);

  const { activeRoomId } = useParams();
  const chainRef = useRef<HTMLButtonElement>(null);
  const showHandler = () => {
    setOpen(!open);
    setShowHash(true);
  };
  const location = useLocation();
  const navigate = useNavigate();
  const { attemptsLeft } = usePrizeBoxStore();

  const [networkOpen, setNetworkOpen] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const { chains } = useSwitchChain();
  const switchChainHandler = useSwitchChainHandler();
  const socialMediaClick = (url: string) => () => {
    window.open(url, '_blank');
    setOpen(false);
  };

  const chainsList = useMemo(
    () =>
      chains.map((chain) => ({
        label: chain.name,
        value: chain.id,
        tooltip: `chains.${chain.id}`,
      })),
    [chains],
  );
  const { setLobbyCreateGameOpen } = useUIStore();

  useEffect(() => {
    if (!open) {
      setNetworkOpen(false);
    }
  }, [open]);
  const screenAddress = useMemo(
    () => prettyTx({ address, leaderboardMap }),
    [address, leaderboardMap],
  );
  const { user, exportWallet } = usePrivy();
  const hasEmbeddedWallet = useMemo(
    () =>
      user &&
      user.linkedAccounts &&
      !!user.linkedAccounts.find(
        (account) =>
          account.type === 'wallet' &&
          account.walletClientType === 'privy' &&
          account.chainType === 'ethereum',
      ),
    [user],
  );

  const handleCopyAddress = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      e.stopPropagation();
      if (address) {
        navigator.clipboard.writeText(address);
        setShowCopiedToast(true);
        setTimeout(() => setShowCopiedToast(false), 1000);
      }
    },
    [address],
  );

  return createPortal(
    <Transition
      show={open}
      enter="transition-opacity duration-500"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-500"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      as={Fragment}
    >
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setOpen(false)}>
        <Transition.Child
          enter="transition ease-in-out duration-500 transform"
          enterFrom="translate-x-full"
          enterTo="translate-x-0"
          leave="transition ease-in-out duration-500 transform"
          leaveFrom="translate-x-0"
          leaveTo="translate-x-full"
          as={Fragment}
        >
          <div className="relative ml-[30%] h-[100%] w-[70%] overflow-auto bg-[#0D1450] px-[25px] py-6 md:ml-auto md:w-[488px]">
            <div className="flex gap-6">
              <InfiniteMaxProgressBar value={Number(pointBalance)} showAttention />
              <button className="" onClick={() => setOpen(false)}>
                <CloseXIcon data-testid="close-icon" className="h-[14px] w-[14px]" />
              </button>
            </div>

            <ul className="mt-4">
              {isConnected && (
                <div className="rounded-[1.125rem] bg-move-icon-border p-[2px]">
                  <ul className="overflow-hidden rounded-[1.125rem]">
                    <li className="flex items-center gap-2.5 bg-[linear-gradient(180deg,_#070437_0%,_#030220_100%)] p-[0.625rem]">
                      <div
                        className={`fixed bottom-8 left-1/2 -translate-x-1/2 transform rounded-lg bg-indigo px-6 py-3 font-poppins text-[16px] text-white transition-opacity duration-300 ${showCopiedToast ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
                      >
                        Copied
                      </div>
                      <Avatar
                        userAddress={address?.toLowerCase() as `0x${string}`}
                        className="h-5 w-5 cursor-pointer rounded-full md:block md:h-10 md:w-10"
                      />
                      <div className="relative flex items-center gap-2.5 font-[600] text-orange-btn md:gap-5 md:text-[20px]">
                        {screenAddress}
                        <Copy
                          className="h-5 w-5 cursor-pointer transition-all hover:opacity-80 active:scale-95 lg:h-7 lg:w-7"
                          onClick={(e) => handleCopyAddress(e)}
                        />
                      </div>
                    </li>
                    {hasEmbeddedWallet && (
                      <li>
                        <button
                          className="flex w-full cursor-pointer items-center justify-between py-2 pl-4 font-semibold"
                          onClick={(e) => {
                            e.stopPropagation();
                            exportWallet();
                          }}
                        >
                          Export my wallet
                        </button>
                      </li>
                    )}
                    <li className="bg-[linear-gradient(180deg,_#0D1450_0%,_#172077_100%)] p-[0.625rem]">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setNetworkOpen(!networkOpen);
                        }}
                        className="flex w-full cursor-pointer items-center justify-between gap-2.5 pe-3 text-[16px] font-[600] text-white md:text-[32px]"
                        ref={chainRef}
                      >
                        <span className="flex items-center gap-2">
                          {chain && chainIcons?.[chain?.id] && (
                            <img src={chainIcons?.[chain?.id]} className="h-5 md:h-10" />
                          )}
                          {chain?.name}
                        </span>

                        <ArrowDown
                          className={`${networkOpen ? 'rotate-180 transform' : ''} w-[16px] transition duration-500 ease-in-out`}
                        />
                      </button>

                      <Dropdown
                        open={networkOpen}
                        list={chainsList}
                        onClick={async (value) => {
                          await switchChainHandler(value);
                          setNetworkOpen(false);
                          if (location.pathname.startsWith(ROUTES.ROOM)) {
                            navigate(ROUTES.LOBBY);
                          }
                        }}
                        active={chainId}
                        position={{ top: 110, left: 0 }}
                      />
                    </li>
                  </ul>
                </div>
              )}

              <MenuItem
                icon={GameIcon}
                text="pages.play"
                href={ROUTES.LOBBY}
                orange
                onClick={() => {
                  setLobbyCreateGameOpen(true);
                  setOpen(false);
                }}
              />
              {!location.pathname.includes('explore-games') && (
                <MenuItem
                  icon={EyeIcon}
                  text="pages.watch"
                  href={ROUTES.EXPLORE_GAMES}
                  onClick={() => {
                    setLobbyCreateGameOpen(true);
                    setOpen(false);
                  }}
                />
              )}
              <MenuItem
                icon={QuestsIcon}
                endIcon={
                  activeQuests > 0 && (
                    <OptimizedImage
                      imageData={attention}
                      alt="attention"
                      className="h-6 w-6 animate-badgePop motion-reduce:animate-none"
                    />
                  )
                }
                href={`${ROUTES.DAILY_QUESTS}`}
                text="pages.quests"
                onClick={() => {
                  setOpen(false);
                }}
              />
              <MenuItem
                icon={CardsIcon}
                endIcon={
                  Number(pointBalance) >= 100 && (
                    <OptimizedImage
                      imageData={attention}
                      alt="attention"
                      className="h-6 w-6 animate-badgePop motion-reduce:animate-none"
                    />
                  )
                }
                text="pages.nfts"
                href={ROUTES.NFTS}
                onClick={() => {
                  setOpen(false);
                }}
              />
              <MenuItem
                icon={LeaderboardIcon}
                text="pages.leaderboard.title"
                href={ROUTES.LEADERBOARD}
                onClick={() => {
                  setOpen(false);
                }}
              />
              <MenuItem
                icon={PriceIcon}
                text="pages.prize"
                href={ROUTES.PRIZE_BOX}
                onClick={() => {
                  setOpen(false);
                }}
                endIcon={
                  attemptsLeft > 0 && (
                    <div className={clsx('relative right-auto top-auto', styles.circle)}>
                      {attemptsLeft}
                    </div>
                  )
                }
              />
              <MenuItem
                icon={AboutIcon}
                text="pages.about"
                href={`${LANDING_PAGE_URL}/rules`}
                external
                onClick={() => {
                  setOpen(false);
                }}
              />

              <MenuItem
                icon={ReferralIcon}
                text="pages.referral"
                href={`${LANDING_PAGE_URL}/referral`}
                external
                onClick={() => {
                  setOpen(false);
                }}
              />

              {activeRoomId && !someoneElseGame && roomStatus !== RoomStatusEnum.Closed && (
                <MenuItem icon={CopyIcon} text="hash.show" external onClick={showHandler} />
              )}

              <li className="flex gap-2 border-b border-border-menu py-[16px] md:py-[26px]">
                <MediaBtn
                  className="mt-[16px] bg-tg"
                  onClick={() => {
                    socialMediaClick(SOCIAL_MEDIA.TELEGRAM)();
                  }}
                >
                  <IconTelegram className="h-[24px] w-[25px] md:w-[30px]" />
                  <span className="hidden md:block">Telegram</span>
                </MediaBtn>
                <MediaBtn
                  className="mt-[16px] bg-discord"
                  onClick={() => {
                    socialMediaClick(SOCIAL_MEDIA.DISCORD)();
                  }}
                >
                  <IconDiscord className="w-[25px] md:h-[24px] md:w-[33px]" />
                  <span className="hidden md:block">Discord</span>
                </MediaBtn>
              </li>
              <li
                className={`flex items-center gap-2.5 border-b border-border-menu py-[16px] md:gap-5 md:py-[26px]`}
              >
                <BugIcon className="h-[20px] w-[20px] text-gray md:h-[28px] md:w-[28px]" />
                <button
                  type="button"
                  className="cursor-pointer font-[600] text-white md:text-[20px]"
                  onClick={(e) => {
                    e.stopPropagation();
                    const target = sentryFeedbackRef?.current;
                    target?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
                  }}
                >
                  {t('common.reportABug')}
                </button>
              </li>
              {isConnected && (
                <MenuItem
                  icon={LogoutIcon}
                  text="common.disconnect"
                  external
                  last
                  onClick={() => {
                    setOpen(false);
                    disconnect();
                  }}
                />
              )}
            </ul>
            {/* Copied toast */}
          </div>
        </Transition.Child>
      </div>
    </Transition>,
    document.body,
  );
};
